import { IErrorResponse } from '@common/api.models';
import { IUpsertProjectDiscussionFileRequest } from '@discussion/projectDiscussion/projectDiscussionFile/projectDiscussionFile.models';
import { IDownloadFileBaseProps } from '@file/file.models';
import { IFileItemBase } from '@gcl/react-core';
import { IProjectTransmittalItem } from '@transmittal/transmittalItem/transmittalItem.models';
import {
  IProjectTransmittalItemRevisionWithItem,
  IUpsertProjectTransmittalItemRevisionRequest,
} from '@transmittal/transmittalItemRevision/transmittalItemRevision.models';
import { EProjectFileType } from './projectFile.constants';

export enum EProjectFileCategory {
  drawings,
  attachments,
}

export interface IProjectFileBase {
  id: number;
  projectId: number;
  transmittalItemRevisionId?: number;
  designBriefId?: number;
  designModificationId?: number;
  loadingRemovalCertId?: number;
  transmittalId?: number;
  uuid: string;
  fileType: EProjectFileType;
  fileName: string;
  url: string;
  addT3chop?: boolean;
  rowVersion: string;
  lastModifiedDateTime: string;
}

export interface IProjectFile extends IProjectFileBase {
  transmittalItemRevision?: IProjectTransmittalItemRevisionWithItem;
}
export interface IUpsertProjectFileRequest {
  id?: number;
  projectId: number;
  isTemp?: boolean;
  uuid: string;
  fileName: string;
  fileType: string;
  url?: string;
  rowVersion?: string;
}

export interface IProjectFileChunkRequest {
  projectId: number;
  uuid: string;
  fileName: string;
  isTemp: boolean;
  fileType?: EProjectFileType;
}
export interface IProjectFileFormCreateRequest extends IProjectFileChunkRequest {}

export interface IProjectFileDrawingTempChunkCompleteRequest extends IProjectFileChunkRequest {}

export interface IProjectFileDrawingChunkCompleteRequest extends IProjectFileChunkRequest {
  lastModifiedDateTime: number | undefined;
  transmittalItem?: IProjectTransmittalItem;
  transmittalId: number;
  transmittalItemRevisionId?: number;
  transmittalItemRevision?: IUpsertProjectTransmittalItemRevisionRequest;
  fileType: EProjectFileType;
  designBriefId?: number;
  addT3chop?: boolean;
  id?: number;
}

export interface IProjectFileAttachmentChunkCompleteRequest extends IProjectFileChunkRequest {
  id?: number;
  fileType?: EProjectFileType;
  designBriefId?: number;
  designModificationId?: number;
  loadingRemovalCertId?: number;
  addT3chop?: boolean;
}

export interface IProjectFileDiscussionAttachmentChunkCompleteRequest extends IProjectFileChunkRequest {
  id?: number;
  discussionFile: IUpsertProjectDiscussionFileRequest;
  fileType?: EProjectFileType;
  addT3chop?: boolean;
}

export interface IFormFileDrawingItem extends IFileItemBase {
  form?: IUpsertProjectTransmittalItemRevisionRequest & {
    projectFile?: {
      id?: number;
      projectId: number;
      isTemp?: boolean;
      fileName: string;
      uuid: string;
      url?: string;
      uploadState: 'uploadItem' | 'uploadRevision' | 'uploadingRevision' | 'uploadingItem' | 'finish' | 'error';
      fileType?: EProjectFileType;
      rowVersion?: string;
    };
    lastModifiedDateTime?: number;
  };

  transmittalItem?: IProjectTransmittalItem;
  fileInfo?: IProjectFileBase;
}

export interface IFormFileAttachmentItem extends IFileItemBase {
  fileInfo?: IProjectFileBase;
  form?: IProjectFileAttachmentChunkCompleteRequest & {
    lastModifiedDate?: Date;
    rowVersion?: string;
  };
}
export interface IProjectFileResponseError {
  message: string;
  errors: IErrorResponse;
}

export interface IProjectFileProcessResponse {
  isSuccess: boolean;
  uuid: string;
  item?: IProjectFile;
  error?: IProjectFileResponseError;
}

export interface IProjectFileDownloadRequest extends IDownloadFileBaseProps {
  type: EProjectFileCategory;
  projectId: number;
  projectFileId: number;
  shouldNotChop?: boolean;
  shouldChopICEApprovalOnly?: boolean;
}

export interface IPdfFileDownloadRequest extends IDownloadFileBaseProps {
  id: number;
  projectId: number;
}

export interface IDownloadTransmittalDrawingsRequest extends IDownloadFileBaseProps {
  projectId: number;
  masterScheduleId: number;
  transmittalId?: number;
  isZip: boolean;
  isPackage: boolean;
  shouldIncludeCoverSheet?: boolean;
  shouldNotChop?: boolean;
  shouldChopICEApprovalOnly?: boolean;
  shouldIncludeIceDesignCertifiedOnly?: boolean;
}

export interface IDownloadDesignOrConstructionDrawingsRequest extends IDownloadFileBaseProps {
  projectId: number;
  masterScheduleId: number;
  isZip: boolean;
  isConstructionDrawing: boolean;
}

export interface IFileResponse {
  file: Blob;
  filename: string;
  mimeType?: string;
}

export interface ICopyProjectFileRequest {
  id: number;
  projectId: number;
}

interface IProjectFileAttachmentsRequestBase {
  projectId: number;
  fileType: EProjectFileType;
}
export interface IProjectFileAttachmentsTransmittalsRequest extends IProjectFileAttachmentsRequestBase {
  transmittalId: number;
}
export interface IProjectFileAttachmentsMasterSchedulesRequest extends IProjectFileAttachmentsRequestBase {
  projectMasterScheduleId: number;
}
export interface IProjectFileAttachmentsDesignBriefsRequest extends IProjectFileAttachmentsRequestBase {
  designBriefId: number;
}

export interface IProjectFileAttachmentsDesignModificationsRequest extends IProjectFileAttachmentsRequestBase {
  designModificationId: number;
}

export interface IProjectFileAttachmentsLoadingRemovalCertsRequest extends IProjectFileAttachmentsRequestBase {
  loadingRemovalCertId: number;
}

export interface IProjectFileAttachmentsTwcGeRequestsRequest extends IProjectFileAttachmentsRequestBase {
  projectTwcGeRequestId: number;
}

export interface IDeleteProjectFileRequest {
  id: number;
  projectId: number;
}

export interface ITempProjectFile {
  projectId: number;
  id: number;
  isTemp: boolean;
  uuid: string;
  fileName: string;
  rowVersion: string;
}

export interface IDownloadAttachmentsRequest extends IDownloadFileBaseProps {
  projectId: number;
  transmittalId?: number;
  designBriefId?: number;
  designModificationId?: number;
  loadingRemovalId?: number;
  fileType?: string;
}

export type TFileDisplay = 'supplementaryFile' | 'comment' | 'reply';

export interface IDownloadDrawingBulkUploadTemplate extends IDownloadFileBaseProps {
  projectId: number;
  transmittalId: number;
  masterScheduleId: number;
}
